/**
 * Quiz Analytics — single dispatch point for every quiz + results GA4 and Meta
 * Pixel event. All gtag/fbq calls in the quiz funnel and the results page route
 * through window.QuizAnalytics, so each event's name and params are defined in
 * exactly one place instead of scattered inline across three files.
 *
 * Scope: GA4 (gtag) + Meta Pixel (fbq) only. The Klaviyo subscription flows stay
 * in quiz-klaviyo.js (async profile sync — a separate concern), and begin_checkout
 * stays in cart.js (cart-domain, loads on non-quiz pages). Both are intentionally
 * out of this module.
 *
 * Value rules (why slugs, not copy): answer copy is client-editable in QA preview,
 * so values key on the stable metaobject handle (fallback: tag), always lowercased
 * so GA4's case-sensitive reports can't split e.g. "salmon" from "Salmon".
 *
 * Load order: this file MUST load before quiz-renderer.js / quiz-klaviyo.js /
 * quiz-results.js. It is self-contained (no deps) and reads window state lazily,
 * so it is safe on both the quiz-modal pages and the results page.
 */
(function () {
  // ── Destination fan-out (the only gtag/fbq guards in the quiz/results surface) ──
  // debug_mode is stamped per event rather than set once on the gtag config: the
  // Google & YouTube channel fires its own config for this measurement ID after
  // ours, and the later call re-configures the target, so a config-level flag is
  // not reliably still in effect by the time these events emit. An event-level
  // parameter travels with the hit and nothing downstream can drop it.
  // Driven by the "Send events in debug mode" theme setting (off by default).
  //
  // send_to is required, not optional: the Google & YouTube channel owns the only
  // gtag container installation on the page, so an event with no explicit
  // destination resolves to none and the hit is never built — it appears in the
  // gtag debug log as `Sending event "x" to undefined` with no request block after
  // it. The channel's own page_view passes send_to for the same reason.
  function ga(name, params) {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const payload = { ...params, send_to: window.GA4_MEASUREMENT_ID };
    if (window.GA4_DEBUG_MODE) payload.debug_mode = true;
    window.gtag("event", name, payload);
  }
  function meta(method, name, params) {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    if (params !== undefined) window.fbq(method, name, params);
    else window.fbq(method, name);
  }

  // ── Value helpers (stable-slug normalization) ──

  // Lowercase + trim any scalar into a stable slug value.
  function slug(value) {
    return String(value ?? "").trim().toLowerCase();
  }

  // Resolve one answer's stable slug: prefer its metaobject handle, fall back to
  // its tag (may be a string or array — take the first). Never the editable text.
  function answerSlug(answer) {
    if (!answer) return "";
    const tag = Array.isArray(answer.tag) ? answer.tag[0] : answer.tag;
    return slug(answer.handle || tag);
  }

  // Join a multiselect's selected options into a deduped, sorted, pipe-joined slug
  // string, e.g. "chicken|salmon".
  function joinMulti(selections) {
    return [...new Set((selections || []).map(answerSlug).filter(Boolean))]
      .sort()
      .join("|");
  }

  // One cat's answered questions as { answer_<handle>: "<slug>" }, from live in-quiz
  // state — no pet_<N>_ prefix, because these ride quiz_pet_summary, which is scoped
  // to a single cat. Multiselects are sorted + pipe-joined. Numeric-measurement
  // questions (weight/activity) are handle-less by design and so are excluded.
  function petAnswers(pet) {
    const out = {};
    Object.entries(pet?.answersByHandle || {}).forEach(([handle, entry]) => {
      const value = entry?.multiselect
        ? joinMulti(entry.selections)
        : answerSlug(entry);
      if (value) out[`answer_${handle.replace(/-/g, "_")}`] = value;
    });
    return out;
  }

  // Fire one quiz_pet_summary per answered cat, carrying that cat's final answer set
  // + the quiz outcome. Deliberately NOT merged into quiz_complete / quiz_abandon: a
  // 5-cat family would blow past GA4's ~25-param-per-event limit and need 30 distinct
  // registered dimensions. Fanning out to one event per cat keeps every cat on the
  // same handful of shared param names, flat regardless of family size.
  function emitPetSummaries(outcome, beacon) {
    const pets = window.OptimealQuizState?.pets || [];
    const quizPetCount = pets.length;
    pets.forEach((pet, i) => {
      const answers = petAnswers(pet);
      if (!Object.keys(answers).length) return; // skip cats with no answers
      const params = {
        event_category: "quiz",
        quiz_outcome: outcome, // "complete" | "abandon"
        pet_number: i + 1,
        quiz_pet_count: quizPetCount,
        ...answers,
      };
      if (beacon) params.transport_type = "beacon";
      ga("quiz_pet_summary", params);
    });
  }

  // ── Semantic events — each defines its GA + Meta names/params in one place ──
  const QuizAnalytics = {
    // Exposed for callers that need slugging outside an event (e.g. results diff).
    slug,
    answerSlug,
    joinMulti,
    petAnswers,

    open() {
      ga("quiz_open", { event_category: "quiz" });
      meta("trackCustom", "QuizOpen");
    },

    step({ stepNumber, stepTitle, questionId }) {
      ga("quiz_step", {
        event_category: "quiz",
        step_number: stepNumber,
        step_title: stepTitle || "",
        // Stable slug alongside the editable title.
        question_id: questionId || "",
      });
      meta("trackCustom", "QuizStep", {
        step_number: stepNumber,
        step_title: stepTitle || "",
      });
    },

    // One event per answered question, per cat (multiselect: once per selected
    // option). Emitted by quiz-renderer's emitStepAnswers() at Continue, not on
    // click — see that method for why. Resolves the stable slug and skips when
    // none is available (never emits copy).
    //
    // petNumber is 1-based, and null on the family-size question, which is asked
    // once for the household before any cat exists. Omitted rather than sent as
    // null in that case: GA4 would store the string "null" as a real dimension
    // value and it would sit alongside "1"/"2" in reports as if it were a cat.
    answer({ questionNumber, questionId, petNumber, answer }) {
      const value = answerSlug(answer);
      if (!value) return;
      const params = {
        event_category: "quiz",
        question_number: questionNumber,
        question_id: questionId,
        answer_value: value,
      };
      if (petNumber != null) params.pet_number = petNumber;
      ga("quiz_answer", params);
    },

    complete({ petCount }) {
      ga("quiz_complete", { event_category: "quiz", quiz_pet_count: petCount });
      meta("track", "Lead", { content_name: "quiz_complete", num_items: petCount });
      // Per-cat answer sets ride their own events (see emitPetSummaries).
      emitPetSummaries("complete");
    },

    // beacon: true routes GA through sendBeacon so the events survive the page
    // unloading (tab-close / navigate-away abandons), where a normal request dies.
    abandon({ lastStep, totalSteps, beacon }) {
      const params = {
        event_category: "quiz",
        quiz_last_step: lastStep,
        quiz_total_steps: totalSteps,
      };
      if (beacon) params.transport_type = "beacon";
      ga("quiz_abandon", params);
      meta("trackCustom", "QuizAbandon", {
        quiz_last_step: lastStep,
        quiz_total_steps: totalSteps,
      });
      emitPetSummaries("abandon", beacon);
    },

    // Save in the results edit modal — the confirmed meal-plan/trial selection.
    mealPlanSelected({ listName, currency, value, items, petName }) {
      ga("select_item", {
        item_list_name: listName,
        currency: currency,
        value: value,
        items: items,
      });
      meta("trackCustom", "MealPlanSelected", {
        pet_name: petName || "",
        num_items: (items || []).length,
      });
    },

    // One event per product entering/leaving the recommended set on a save.
    // Skip when no handle resolves — a handle-less product has no usable identity
    // and would land in GA4 as "(not set)".
    resultsAdded({ productHandle, listName }) {
      const handle = slug(productHandle);
      if (!handle) return;
      ga("results_added", { product_handle: handle, list_name: listName });
    },
    resultsRemoved({ productHandle, listName }) {
      const handle = slug(productHandle);
      if (!handle) return;
      ga("results_removed", { product_handle: handle, list_name: listName });
    },
  };

  window.QuizAnalytics = QuizAnalytics;
})();
